import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, Plus, Edit, Trash2, ArrowLeft, X, Building2, Calendar, Search, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import api from '../services/api';

function AsignacionesSubarrendatarios() {
  const [asignaciones, setAsignaciones] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [subarrendatarios, setSubarrendatarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    empresa_id: '',
    subarrendatario_id: '',
    matricula: '',
    fecha_inicio: '',
    fecha_fin: '',
    contrato_referencia: '',
    observaciones: ''
  });
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState({ empresa_id: '', subarrendatario_id: '', matricula: '', vigentes: false });
  const navigate = useNavigate();

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    if (filtro.empresa_id) {
      cargarSubarrendatarios(filtro.empresa_id);
    }
  }, [filtro.empresa_id]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [asigRes, empRes] = await Promise.all([
        api.get('/admin/asignaciones-subarrendatarios'),
        api.get('/admin/empresas')
      ]);
      setAsignaciones(asigRes.data.data || []);
      setEmpresas(empRes.data.data || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarSubarrendatarios = async (empresaId) => {
    try {
      const response = await api.get('/admin/subarrendatarios', {
        params: { empresa_propietaria_id: empresaId, activo: true }
      });
      setSubarrendatarios(response.data.data || []);
    } catch (error) {
      console.error('Error cargando subarrendatarios:', error);
      setSubarrendatarios([]);
    }
  };

  const buscar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtro.empresa_id) params.empresa_id = filtro.empresa_id;
      if (filtro.subarrendatario_id) params.subarrendatario_id = filtro.subarrendatario_id;
      if (filtro.matricula) params.matricula = filtro.matricula;
      if (filtro.vigentes) params.vigentes = true;

      const response = await api.get('/admin/asignaciones-subarrendatarios', { params });
      setAsignaciones(response.data.data || []);
    } catch (error) {
      console.error('Error buscando:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = async (asig = null) => {
    if (asig) {
      setEditando(asig);
      await cargarSubarrendatarios(asig.empresa_id);
      setFormData({
        empresa_id: asig.empresa_id || '',
        subarrendatario_id: asig.subarrendatario_id || '',
        matricula: asig.matricula || '',
        fecha_inicio: asig.fecha_inicio?.split('T')[0] || '',
        fecha_fin: asig.fecha_fin?.split('T')[0] || '',
        contrato_referencia: asig.contrato_referencia || '',
        observaciones: asig.observaciones || ''
      });
    } else {
      setEditando(null);
      if (filtro.empresa_id) {
        await cargarSubarrendatarios(filtro.empresa_id);
      }
      setFormData({
        empresa_id: filtro.empresa_id || '',
        subarrendatario_id: filtro.subarrendatario_id || '',
        matricula: '',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: '',
        contrato_referencia: '',
        observaciones: ''
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleEmpresaChange = async (empresaId) => {
    setFormData({ ...formData, empresa_id: empresaId, subarrendatario_id: '' });
    if (empresaId) {
      await cargarSubarrendatarios(empresaId);
    } else {
      setSubarrendatarios([]);
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError('');

    const dataToSend = {
      ...formData,
      fecha_fin: formData.fecha_fin || null
    };

    try {
      if (editando) {
        await api.put(`/admin/asignaciones-subarrendatarios/${editando.id}`, dataToSend);
      } else {
        await api.post('/admin/asignaciones-subarrendatarios', dataToSend);
      }
      setShowModal(false);
      buscar();
    } catch (error) {
      setError(error.response?.data?.error || 'Error guardando asignacion');
    }
  };

  const finalizarAsignacion = async (asig) => {
    const fechaFin = prompt('Fecha de finalizacion (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!fechaFin) return;

    try {
      await api.patch(`/admin/asignaciones-subarrendatarios/${asig.id}/finalizar`, { fecha_fin: fechaFin });
      buscar();
    } catch (error) {
      alert(error.response?.data?.error || 'Error finalizando asignacion');
    }
  };

  const eliminar = async (asig) => {
    if (!window.confirm(`¿Eliminar esta asignacion de ${asig.matricula} a ${asig.subarrendatario_nombre}?`)) return;

    try {
      await api.delete(`/admin/asignaciones-subarrendatarios/${asig.id}`);
      buscar();
    } catch (error) {
      alert(error.response?.data?.error || 'Error eliminando asignacion');
    }
  };

  const esVigente = (asig) => {
    const hoy = new Date().toISOString().split('T')[0];
    return asig.activo && asig.fecha_inicio <= hoy && (!asig.fecha_fin || asig.fecha_fin >= hoy);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-ES');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/80 backdrop-blur-lg shadow-soft border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/subarrendatarios')} className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-display">Asignaciones a Subarrendatarios</h1>
              <p className="text-sm text-slate-600">Gestiona que vehiculos estan cedidos a cada subarrendatario</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin/subarrendatarios')}
              className="flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 rounded-xl hover:bg-violet-200 transition-colors"
            >
              <Building2 className="w-5 h-5" />
              Ver Subarrendatarios
            </button>
            <button
              onClick={() => abrirModal()}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 hover:shadow-glow transition-all"
            >
              <Plus className="w-5 h-5" />
              Nueva Asignacion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Filtros */}
        <div className="bg-white rounded-2xl shadow-soft p-4 mb-6 border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Empresa Propietaria</label>
              <select
                value={filtro.empresa_id}
                onChange={(e) => setFiltro({...filtro, empresa_id: e.target.value, subarrendatario_id: ''})}
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todas las empresas</option>
                {empresas.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subarrendatario</label>
              <select
                value={filtro.subarrendatario_id}
                onChange={(e) => setFiltro({...filtro, subarrendatario_id: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                disabled={!filtro.empresa_id}
              >
                <option value="">Todos</option>
                {subarrendatarios.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.razon_social}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Matricula</label>
              <input
                type="text"
                value={filtro.matricula}
                onChange={(e) => setFiltro({...filtro, matricula: e.target.value.toUpperCase()})}
                placeholder="1234ABC"
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filtro.vigentes}
                  onChange={(e) => setFiltro({...filtro, vigentes: e.target.checked})}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-slate-700">Solo vigentes</span>
              </label>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={buscar}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
              >
                <Search className="w-4 h-4" />
                Buscar
              </button>
              <button
                onClick={() => { setFiltro({ empresa_id: '', subarrendatario_id: '', matricula: '', vigentes: false }); cargarDatos(); }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Cargando...</p>
          </div>
        ) : asignaciones.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-soft p-12 text-center border border-slate-200">
            <Car className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No hay asignaciones</h3>
            <p className="text-slate-600 mb-4">Asigna vehiculos a subarrendatarios para que el sistema pueda identificarlos automaticamente.</p>
            <button
              onClick={() => abrirModal()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
            >
              <Plus className="w-5 h-5" />
              Nueva Asignacion
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Matricula</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Subarrendatario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Empresa</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Periodo</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {asignaciones.map((asig) => (
                  <tr key={asig.id} className={!asig.activo ? 'bg-slate-50' : 'hover:bg-slate-50 transition-colors'}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                          <Car className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="font-mono font-medium text-slate-900">{asig.matricula}</div>
                          {asig.contrato_referencia && (
                            <div className="text-xs text-slate-500">Ref: {asig.contrato_referencia}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-900">{asig.subarrendatario_nombre}</div>
                      <div className="text-xs text-slate-500">{asig.subarrendatario_cif}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{asig.empresa_nombre}</td>
                    <td className="px-6 py-4 text-center">
                      <div className="text-sm text-slate-900">
                        <span className="font-medium">{formatFecha(asig.fecha_inicio)}</span>
                        <span className="text-slate-400 mx-2">→</span>
                        <span className={asig.fecha_fin ? 'font-medium' : 'text-emerald-600'}>{asig.fecha_fin ? formatFecha(asig.fecha_fin) : 'Vigente'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {esVigente(asig) ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3" />
                          Vigente
                        </span>
                      ) : asig.activo ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <Clock className="w-3 h-3" />
                          Finalizada
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          Cancelada
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {esVigente(asig) && (
                          <button
                            onClick={() => finalizarAsignacion(asig)}
                            className="p-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Finalizar asignacion"
                          >
                            <Calendar className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => abrirModal(asig)}
                          className="p-2 text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => eliminar(asig)}
                          className="p-2 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">{editando ? 'Editar Asignacion' : 'Nueva Asignacion'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-slate-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={guardar} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Empresa Propietaria *</label>
                <select
                  value={formData.empresa_id}
                  onChange={(e) => handleEmpresaChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={!!editando}
                >
                  <option value="">Seleccionar empresa...</option>
                  {empresas.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subarrendatario *</label>
                <select
                  value={formData.subarrendatario_id}
                  onChange={(e) => setFormData({...formData, subarrendatario_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={!formData.empresa_id}
                >
                  <option value="">Seleccionar subarrendatario...</option>
                  {subarrendatarios.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.razon_social} ({sub.cif})</option>
                  ))}
                </select>
                {!formData.empresa_id && (
                  <p className="text-xs text-slate-500 mt-1">Selecciona primero una empresa</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Matricula *</label>
                <input
                  type="text"
                  value={formData.matricula}
                  onChange={(e) => setFormData({...formData, matricula: e.target.value.toUpperCase().replace(/\s/g, '')})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  required
                  placeholder="1234ABC"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Inicio *</label>
                  <input
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) => setFormData({...formData, fecha_inicio: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Fin</label>
                  <input
                    type="date"
                    value={formData.fecha_fin}
                    onChange={(e) => setFormData({...formData, fecha_fin: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Deja vacio si la asignacion sigue vigente</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Referencia Contrato</label>
                <input
                  type="text"
                  value={formData.contrato_referencia}
                  onChange={(e) => setFormData({...formData, contrato_referencia: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  placeholder="CONTRATO-2024-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Observaciones</label>
                <textarea
                  value={formData.observaciones}
                  onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  rows="2"
                  placeholder="Notas adicionales..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all"
                >
                  {editando ? 'Guardar Cambios' : 'Crear Asignacion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AsignacionesSubarrendatarios;
